<template>
  <!-- Header -->
  <header class="bg-gray-900 border-b border-gray-700">
    <div class="max-w-full mx-auto px-4 py-3 flex items-center justify-end">
      <!-- Actions -->
      <div class="flex items-center space-x-3 text-gray-300">
        <button
          v-if="readNotifications > 0"
          @click="deleteAllRead"
          class="flex items-center bg-red-600 hover:bg-red-500 text-sm px-3 py-2 rounded-lg transition-colors border border-red-700"
        >
          <i class="fas fa-trash mr-1"></i> ลบที่อ่านแล้ว
        </button>
        <button
          @click="openMessageModal"
          class="flex items-center bg-indigo-600 hover:bg-indigo-500 text-sm px-3 py-2 rounded-lg transition-colors border border-indigo-700"
        >
          <i class="fas fa-envelope mr-1"></i> ส่งข้อความ
        </button>
      </div>
    </div>
  </header>
  
  <div class="p-3 pb-20 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
     <!-- Discord CTA Section -->
      <div class="bg-gray-900 bg-opacity-70 p-4 rounded-lg border border-indigo-700 mb-6 flex flex-col sm:flex-row items-center justify-between transition-all hover:bg-opacity-80">
        <div class="flex items-center space-x-3">
          <i class="fab fa-discord text-indigo-400 text-3xl"></i>
          <div>
            <h3 class="text-lg font-semibold text-white">เข้าร่วมชุมชนของเรา!</h3>
            <p class="text-gray-300 text-sm">
              เข้าร่วมดิสคอร์ดเพื่อพูดคุย แลกเปลี่ยน ซื้อขาย และอ่านวิธีการเล่นจากผู้เล่นอื่น
            </p>
          </div>
        </div>
        <a
          href="https://discord.gg/UA9pNFWwCe"
          target="_blank"
          class="bg-indigo-600 hover:bg-indigo-500 text-white text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 mt-3 sm:mt-0 flex items-center"
        >
          <i class="fab fa-discord mr-2"></i> เข้าร่วมดิสคอร์ด
        </a>
      </div>
    <!-- Main Content -->
    <main class="max-w-full mx-auto ">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center h-64">
        <i class="fas fa-spinner text-4xl text-indigo-400 animate-spin"></i>
      </div>

      <!-- Error / Success Messages -->
      <div v-if="error" class="bg-red-900 bg-opacity-50 p-3 rounded-lg text-center text-red-300 border border-red-700">
        {{ error }}
      </div>
      <div v-if="successMessage" class="bg-green-900 bg-opacity-50 p-3 rounded-lg text-center text-green-300 border border-green-700">
        {{ successMessage }}
      </div>

      <!-- Filter and Actions -->
      <div class="flex flex-col sm:flex-row justify-between items-center bg-gray-900 bg-opacity-70 p-3 rounded-lg border border-gray-800">
        <select
          v-model="typeFilter"
          class="bg-gray-900 bg-opacity-70 p-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600"
        >
          <option value="all">ทั้งหมด</option>
          <option value="assassination">การลอบสังหาร</option>
          <option value="comment">ความคิดเห็น</option>
          <option value="occupier">การยึดครอง</option>
          <option value="message">ข้อความ</option>
        </select>
      </div>

      <!-- Notifications List -->
      <div v-if="!loading && filteredNotifications.length > 0" class="space-y-4">
        <div
          v-for="notification in filteredNotifications"
          :key="notification.id"
          class="bg-gray-900 bg-opacity-70 p-4 rounded-lg border border-gray-800"
          :class="{ 'cursor-pointer hover:bg-gray-800': notification.type === 'comment' && notification.postId }"
          @click="notification.type === 'comment' && notification.postId ? openPostModal(notification.postId) : null"
        >
          <div class="flex items-start">
            <i
              :class="getIcon(notification.type, notification.read)"
              class="mr-3 mt-1"
            ></i>
            <div class="flex-1">
              <p class="text-gray-400 text-sm mt-1">
                {{ notification.type === 'message' && notification.from ? `จาก ${notification.from}: ` : '' }}
                {{ notification.message || 'ไม่มีข้อความ' }}
              </p>
              <p v-if="notification.details" class="text-gray-300 text-sm mt-2 bg-gray-700 p-3 rounded-lg">
                {{ notification.details }}
              </p>
              <p class="text-gray-500 text-xs mt-2">{{ formatDate(notification.createdAt) }}</p>
            </div>
            <button
              @click.stop="deleteNotification(notification.id)"
              class="ml-3 text-red-400 hover:text-red-300"
              title="ลบการแจ้งเตือน"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
      <div v-else-if="!loading" class="bg-gray-800 p-4 rounded-lg text-center text-gray-400 border border-gray-700">
        ไม่มีแจ้งเตือนในขณะนี้
      </div>

      <!-- Load More -->
      <div v-if="loadingMore" class="flex justify-center mt-4">
        <i class="fas fa-spinner text-4xl text-indigo-400 animate-spin"></i>
      </div>
      <div v-if="hasMore && !loadingMore" class="mt-4 text-center w-full">
        <button
          @click="loadMore"
          class="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700"
        >
          โหลดเพิ่มเติม
        </button>
      </div>

      <!-- Post Modal -->
      <transition name="fade">
        <div v-if="showPostModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
          <div class="w-full max-w-lg m-4 bg-gray-800 rounded-lg border border-gray-700 shadow-xl flex flex-col max-h-[80vh]">
            <div class="p-4 border-b border-gray-700">
              <h2 class="text-lg font-semibold text-white">{{ post.characterName }}</h2>
              <div class="flex items-center space-x-4">
                <p class="text-gray-500 text-xs">{{ formatDate(post.createdAt) }}</p>
                <p class="text-gray-500 text-xs">ความคิดเห็น: {{ post.commentCount || 0 }}</p>
              </div>
            </div>
            <div class="p-4 space-y-4 flex-1 overflow-y-auto">
              <!-- Post Content -->
              <div>
                <p class="text-gray-300 text-sm bg-gray-700 p-3 rounded-lg">{{ post.content }}</p>
              </div>
              <!-- Comments -->
              <h3 class="text-sm font-semibold text-gray-200">ความคิดเห็น</h3>
              <div v-if="comments.length > 0" :class="[comments.length >= 3 ? 'h-64' : 'h-20']" class="space-y-3 overflow-auto" id="style-1">
                <div v-for="comment in comments" :key="comment.id" class="flex items-start bg-gray-900 p-3 rounded bg-opacity-70 relative">
                  <img :src="comment.characterImageUrl" alt="Character Avatar" class="w-8 h-8 rounded-full mr-3" />
                  <div class="flex-1">
                    <p class="font-semibold text-sm text-gray-300">{{ comment.characterName }}</p>
                    <p class="text-gray-400 text-sm mt-1">{{ comment.content }}</p>
                    <p class="text-gray-500 text-xs mt-2">{{ formatDate(comment.createdAt) }}</p>
                  </div>
                  <button
                    v-if="isAdmin"
                    @click.stop="deleteComment(comment.id, post.id)"
                    :disabled="isPosting"
                    class="absolute top-2 right-2 text-red-400 hover:text-red-300 text-sm"
                    title="ลบความคิดเห็น (สำหรับแอดมิน)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
              <div v-else class="text-gray-400 text-sm">ยังไม่มีความคิดเห็น</div>
              <!-- Add Comment -->
              <div>
                <h3 class="text-sm font-semibold text-gray-200 mb-2">เพิ่มความคิดเห็น</h3>
                <textarea
                  v-model="newComment"
                  placeholder="เขียนความคิดเห็น..."
                  class="bg-gray-700 p-3 rounded-lg text-sm w-full h-16 focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600 mb-3"
                ></textarea>
                <button
                  @click="submitComment"
                  :disabled="!newComment || commentLoading"
                  class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 flex items-center justify-center"
                >
                  <i v-if="commentLoading" class="fas fa-spinner animate-spin text-base mr-2"></i>
                  <span v-if="!commentLoading">โพสต์ความคิดเห็น</span>
                </button>
              </div>
            </div>
            <div class="p-4 border-t border-gray-700 flex justify-end space-x-3 sticky bottom-0 bg-gray-800">
              <button
                v-if="isAdmin"
                @click="deletePost(post.id)"
                :disabled="commentLoading"
                class="bg-red-600 hover:bg-red-500 text-sm px-3 py-2 rounded-lg transition-colors border border-red-700 disabled:opacity-50"
                title="ลบโพสต์ (สำหรับแอดมิน)"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
              <button
                @click="closePostModal"
                :disabled="commentLoading"
                class="bg-gray-600 hover:bg-gray-500 text-sm px-4 py-2 rounded-lg transition-colors border border-gray-700 disabled:opacity-50"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Message Modal -->
      <transition name="fade">
        <div v-if="showMessageModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
          <div class="w-full max-w-md m-4 bg-gray-800 rounded-lg border border-gray-700 shadow-xl flex flex-col max-h-[80vh]">
            <div class="p-4 border-b border-gray-700">
              <h2 class="text-lg font-semibold text-white">ส่งข้อความ</h2>
            </div>
            <div class="p-4 space-y-4 flex-1 overflow-y-auto">
              <input
                v-model="recipientName"
                placeholder="ชื่อตัวละครผู้รับ"
                class="bg-gray-700 p-3 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600 mb-3"
              />
              <textarea
                v-model="messageContent"
                placeholder="ข้อความ..."
                class="bg-gray-700 p-3 rounded-lg text-sm w-full h-32 focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600 mb-3"
              ></textarea>
            </div>
            <div class="p-4 border-t border-gray-700 flex justify-end space-x-3">
              <button
                @click="closeMessageModal"
                class="bg-gray-600 hover:bg-gray-500 text-sm px-4 py-2 rounded-lg transition-colors border border-gray-700"
              >
                ปิด
              </button>
              <button
                @click="sendMessage"
                :disabled="!recipientName || !messageContent || sendingMessage"
                class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 flex items-center"
              >
                <i v-if="sendingMessage" class="fas fa-spinner animate-spin mr-2"></i>
                ส่ง
              </button>
            </div>
          </div>
        </div>
      </transition>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFirestore, projectFunctions, FieldValue } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'Notifications',
  setup() {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const notifications = ref([]);
    const loading = ref(true);
    const loadingMore = ref(false);
    const hasMore = ref(true);
    const lastVisible = ref(null);
    const error = ref(null);
    const successMessage = ref('');
    const typeFilter = ref('all');
    let unsubscribeComments = null;

    // Post Modal State
    const showPostModal = ref(false);
    const post = ref(null);
    const comments = ref([]);
    const newComment = ref('');
    const commentLoading = ref(false);
    const happyLevel = ref(0);
    const happyFromComment = ref(0);
    const happyFromPost = ref(0);
    const characterCreated = ref(false);
    const isLive = ref(false);
    const isAdmin = ref(false);

    // Message Modal State
    const showMessageModal = ref(false);
    const recipientName = ref('');
    const messageContent = ref('');
    const sendingMessage = ref(false);

    // Computed properties
    const filteredNotifications = computed(() => {
      const occupierTypes = [
        'challengeOccupier',
        'clearOccupier',
        'occupierChallenged',
        'occupierClearAttempt',
        'occupierCleared',
        'occupierLost',
        'challengerDefeated',
        'challengerFled',
        'resignOccupier'
      ];
      return notifications.value
        .filter(notification => {
          if (typeFilter.value === 'all') return true;
          if (typeFilter.value === 'occupier') return occupierTypes.includes(notification.type);
          if (typeFilter.value === 'message') return notification.type === 'message';
          return notification.type === typeFilter.value;
        })
        .sort((a, b) => {
          const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
          const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
          return dateB - dateA;
        });
    });

    const readNotifications = computed(() => {
      return notifications.value.filter(n => n.read).length;
    });

    // Get icon based on notification type and read status
    const getIcon = (type, read) => {
      const baseClass = read ? 'text-gray-500' : 'text-yellow-400';
      switch (type) {
        case 'assassination':
          return `fas fa-skull-crossbones ${baseClass}`;
        case 'comment':
          return `fas fa-comment ${baseClass}`;
        case 'system':
          return `fas fa-cog ${baseClass}`;
        case 'event':
          return `fas fa-calendar ${baseClass}`;
        case 'purchase':
          return `fas fa-shopping-cart ${baseClass}`;
        case 'challengeOccupier':
        case 'clearOccupier':
        case 'occupierChallenged':
        case 'occupierClearAttempt':
        case 'occupierCleared':
        case 'occupierLost':
        case 'challengerDefeated':
        case 'challengerFled':
        case 'resignOccupier':
          return `fas fa-flag ${baseClass}`;
        case 'message':
          return `fas fa-envelope ${baseClass}`;
        default:
          return `fas fa-bell ${baseClass}`;
      }
    };

    // Format date to Thai format
    const formatDate = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      let date;
      try {
        date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      } catch (e) {
        console.error('Invalid timestamp:', timestamp, e);
        return 'วันที่ไม่ถูกต้อง';
      }
      if (isNaN(date.getTime())) return 'วันที่ไม่ถูกต้อง';
      return date.toLocaleString('th-TH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).replace(',', '');
    };

    watch(() => playerStore.userData, (data) => {
      if (data) {
        characterCreated.value = data.characterCreated === true;
        isAdmin.value = data.admin === true;
      } else {
        characterCreated.value = false;
        isAdmin.value = false;
      }
    }, { immediate: true });

    watch(() => playerStore.characterPrivateData, (data) => {
      if (data) {
        happyLevel.value = data.happyLevel || 0;
        happyFromComment.value = data.happyFromComment || 0;
        happyFromPost.value = data.happyFromPost || 0;
        isLive.value = data.isLive ?? false;
      } else {
        happyLevel.value = 0;
        happyFromComment.value = 0;
        happyFromPost.value = 0;
        isLive.value = false;
      }
    }, { immediate: true });

    // Mark all unread notifications as read
    const markAllAsRead = async () => {
      if (!user.value) return;
      try {
        const unreadQuery = projectFirestore
          .collection('notifications')
          .where('userId', '==', user.value.uid)
          .where('read', '==', false);
        const unreadSnapshot = await unreadQuery.get();
        if (unreadSnapshot.empty) return;

        const batch = projectFirestore.batch();
        unreadSnapshot.docs.forEach((doc) => {
          batch.update(doc.ref, {
            read: true,
            updatedAt: new Date(),
          });
        });
        await batch.commit();
        successMessage.value = 'ทำเครื่องหมายว่าอ่านทั้งหมดสำเร็จ';
      } catch (err) {
        console.error('Error marking notifications as read:', err);
        error.value = 'เกิดข้อผิดพลาดในการทำเครื่องหมายว่าอ่าน';
      }
    };

    // Fetch initial notifications (first 10)
    const fetchNotifications = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อดูการแจ้งเตือน';
        loading.value = false;
        return;
      }
      try {
        const query = projectFirestore
          .collection('notifications')
          .where('userId', '==', user.value.uid)
          .orderBy('createdAt', 'desc')
          .limit(10);
        const snapshot = await query.get();
        notifications.value = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        if (snapshot.docs.length > 0) {
          lastVisible.value = snapshot.docs[snapshot.docs.length - 1];
        }
        if (snapshot.docs.length < 10) {
          hasMore.value = false;
        }
        loading.value = false;
        error.value = null;
      } catch (err) {
        console.error('Error fetching notifications:', err);
        error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลการแจ้งเตือน';
        loading.value = false;
      }
    };

    // Load more notifications (next 10)
    const loadMore = async () => {
      if (!hasMore.value || loadingMore.value) return;
      loadingMore.value = true;
      try {
        const nextQuery = projectFirestore
          .collection('notifications')
          .where('userId', '==', user.value.uid)
          .orderBy('createdAt', 'desc')
          .startAfter(lastVisible.value)
          .limit(10);
        const nextSnapshot = await nextQuery.get();
        const newNotifications = nextSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        notifications.value = [...notifications.value, ...newNotifications];
        if (nextSnapshot.docs.length > 0) {
          lastVisible.value = nextSnapshot.docs[nextSnapshot.docs.length - 1];
        }
        if (nextSnapshot.docs.length < 10) {
          hasMore.value = false;
        }
        error.value = null;
      } catch (err) {
        console.error('Error loading more notifications:', err);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลเพิ่มเติม';
      } finally {
        loadingMore.value = false;
      }
    };

    // Open post modal and fetch post and comments data
    const openPostModal = async (postId) => {
      if (!postId) {
        error.value = 'ไม่มีรหัสโพสต์';
        return;
      }
      post.value = null;
      comments.value = [];
      try {
        // Fetch post
        const postDoc = await projectFirestore.collection('posts').doc(postId).get();
        if (!postDoc.exists) {
          error.value = 'ไม่พบโพสต์';
          return;
        }
        post.value = {
          id: postDoc.id,
          ...postDoc.data(),
        };

        // Fetch comments
        unsubscribeComments = projectFirestore
          .collection('posts')
          .where('parentId', '==', postId)
          .orderBy('createdAt', 'asc')
          .onSnapshot(
            (commentSnapshot) => {
              comments.value = commentSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
              }));
            },
            (err) => {
              console.error('Error fetching comments:', err);
              error.value = 'เกิดข้อผิดพลาดในการดึงคอมเมนต์';
            }
          );

        showPostModal.value = true;
      } catch (err) {
        console.error('Error fetching post or comments:', err);
        error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลโพสต์หรือความคิดเห็น';
      }
    };

    // Close post modal
    const closePostModal = () => {
      showPostModal.value = false;
      post.value = null;
      newComment.value = '';
      if (unsubscribeComments) {
        unsubscribeComments();
        unsubscribeComments = null;
      }
    };

    // Submit comment
    const submitComment = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อแสดงความคิดเห็น';
        return;
      }
      if (!post.value || !newComment.value.trim()) {
        error.value = 'กรุณากรอกความคิดเห็น';
        return;
      }
      if (!characterCreated.value) {
        error.value = 'คุณต้องสร้างตัวละครก่อนเพื่อคอมเมนต์';
        return;
      }
      if (!isLive.value) {
        error.value = 'ตัวละครของคุณต้องมีสถานะ isLive เพื่อคอมเมนต์';
        return;
      }

      commentLoading.value = true;
      try {
        const createPost = projectFunctions.httpsCallable('createPost');
        const result = await createPost({
          content: newComment.value.trim(),
          type: 'comment',
          parentId: post.value.id,
        });
        const prevHappyFromComment = happyFromComment.value;
        happyFromComment.value = result.data.happyFromComment;
        happyLevel.value = result.data.happyLevel;
        happyFromPost.value = result.data.happyFromPost;
        const isOwnPost = post.value.userId === user.value.uid;
        const remainingComments = 3 - happyFromComment.value;
        successMessage.value = result.data.happyLevelReason
          ? `คอมเมนต์สำเร็จเป็น ${result.data.characterName}! ${result.data.happyLevelReason}`
          : `คอมเมนต์สำเร็จเป็น ${result.data.characterName}! ${
              !isOwnPost && prevHappyFromComment < 3 && result.data.happyLevelIncrement > 0
                ? `ได้รับความสุข +1 (เหลือ ${remainingComments}/3)`
                : ''
            }`.trim();
        newComment.value = '';
      } catch (err) {
        console.error('Error submitting comment:', err);
        error.value = `เกิดข้อผิดพลาดในการส่งความคิดเห็น: ${err.message}`;
      } finally {
        commentLoading.value = false;
      }
    };

    // Delete all read notifications
    const deleteAllRead = async () => {
      if (!user.value) return;
      try {
        const readQuery = projectFirestore
          .collection('notifications')
          .where('userId', '==', user.value.uid)
          .where('read', '==', true);
        const readSnapshot = await readQuery.get();
        if (readSnapshot.empty) return;

        const batch = projectFirestore.batch();
        readSnapshot.docs.forEach((doc) => batch.delete(doc.ref));
        await batch.commit();

        // Refresh the list after deletion
        notifications.value = [];
        lastVisible.value = null;
        hasMore.value = true;
        await fetchNotifications();

        successMessage.value = 'ลบการแจ้งเตือนที่อ่านแล้วทั้งหมดสำเร็จ';
        error.value = null;
      } catch (err) {
        console.error('Error deleting read notifications:', err);
        error.value = 'เกิดข้อผิดพลาดในการลบการแจ้งเตือน';
      }
    };

    // Delete single notification
    const deleteNotification = async (notificationId) => {
      if (!user.value || !notificationId) {
        error.value = 'การแจ้งเตือนไม่ถูกต้อง';
        return;
      }
      try {
        await projectFirestore.collection('notifications').doc(notificationId).delete();
        // Remove from local list
        notifications.value = notifications.value.filter(n => n.id !== notificationId);
        successMessage.value = 'ลบการแจ้งเตือนสำเร็จ';
        error.value = null;
      } catch (err) {
        console.error('Error deleting notification:', err);
        error.value = 'เกิดข้อผิดพลาดในการลบการแจ้งเตือน';
      }
    };

    // Delete post (admin only)
    const deletePost = async (postId) => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อลบโพสต์';
        return;
      }
      if (!isAdmin.value) {
        error.value = 'คุณไม่มีสิทธิ์ลบโพสต์';
        return;
      }
      commentLoading.value = true;
      try {
        const postRef = projectFirestore.collection('posts').doc(postId);
        const postDoc = await postRef.get();
        if (!postDoc.exists) {
          throw new Error('ไม่พบโพสต์');
        }

        // Delete post and its comments
        const commentsQuery = await projectFirestore
          .collection('posts')
          .where('parentId', '==', postId)
          .get();

        const batch = projectFirestore.batch();
        batch.delete(postRef);
        commentsQuery.docs.forEach(doc => batch.delete(doc.ref));
        await batch.commit();

        successMessage.value = 'ลบโพสต์สำเร็จ';
        closePostModal();
      } catch (err) {
        console.error('Error deleting post:', err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการลบโพสต์';
      } finally {
        commentLoading.value = false;
      }
    };

    // Delete comment (admin only)
    const deleteComment = async (commentId, parentPostId) => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อลบความคิดเห็น';
        return;
      }
      if (!isAdmin.value) {
        error.value = 'คุณไม่มีสิทธิ์ลบความคิดเห็น';
        return;
      }
      commentLoading.value = true;
      try {
        const commentRef = projectFirestore.collection('posts').doc(commentId);
        const commentDoc = await commentRef.get();
        if (!commentDoc.exists) {
          throw new Error('ไม่พบความคิดเห็น');
        }

        // Delete the comment
        await commentRef.delete();

        // Update commentCount in parent post
        const postRef = projectFirestore.collection('posts').doc(parentPostId);
        await postRef.update({
          commentCount: FieldValue.increment(-1)
        });

        successMessage.value = 'ลบความคิดเห็นสำเร็จ';
      } catch (err) {
        console.error('Error deleting comment:', err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการลบความคิดเห็น';
      } finally {
        commentLoading.value = false;
      }
    };

    // Open message modal
    const openMessageModal = () => {
      showMessageModal.value = true;
      recipientName.value = '';
      messageContent.value = '';
    };

    // Close message modal
    const closeMessageModal = () => {
      showMessageModal.value = false;
    };

    // Send message
    const sendMessage = async () => {
      if (!recipientName.value.trim() || !messageContent.value.trim()) {
        error.value = 'กรุณากรอกชื่อผู้รับและข้อความ';
        return;
      }
      if (!characterCreated.value) {
        error.value = 'คุณต้องสร้างตัวละครก่อนเพื่อส่งข้อความ';
        return;
      }
      if (!isLive.value) {
        error.value = 'ตัวละครของคุณต้องมีสถานะมีชีวิตเพื่อส่งข้อความ';
        return;
      }
      const trimmedMessage = messageContent.value.trim();
      const trimmedRecipient = recipientName.value.trim();
      if (trimmedMessage.length > 200) {
        error.value = 'ข้อความยาวเกิน 200 ตัวอักษร';
        return;
      }
      if (trimmedRecipient.length > 10) {
        error.value = 'ชื่อผู้รับยาวเกิน 10 ตัวอักษร';
        return;
      }

      sendingMessage.value = true;
      error.value = null;
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        const result = await characterActions({
          action: 'characterActions',
          subAction: 'sendMessage',
          recipientName: trimmedRecipient,
          message: trimmedMessage,
        });
        successMessage.value = result.data.message;
        messageContent.value = '';
        recipientName.value = '';
        closeMessageModal();
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการส่งข้อความ';
      } finally {
        sendingMessage.value = false;
      }
    };

    onMounted(async () => {
      await markAllAsRead();
      await fetchNotifications();
    });

    onUnmounted(() => {
      if (unsubscribeComments) unsubscribeComments();
    });

    return {
      user,
      notifications,
      filteredNotifications,
      loading,
      loadingMore,
      hasMore,
      error,
      successMessage,
      typeFilter,
      readNotifications,
      formatDate,
      getIcon,
      deleteAllRead,
      deleteNotification,
      showPostModal,
      post,
      comments,
      newComment,
      commentLoading,
      openPostModal,
      closePostModal,
      submitComment,
      deletePost,
      deleteComment,
      happyLevel,
      happyFromComment,
      happyFromPost,
      characterCreated,
      isLive,
      isAdmin,
      loadMore,
      showMessageModal,
      recipientName,
      messageContent,
      sendingMessage,
      openMessageModal,
      closeMessageModal,
      sendMessage,
    };
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.font-orbitron {
  font-family: 'Orbitron', sans-serif;
}
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fcondo.png?alt=media&token=198ebb1c-66e5-4bf0-aa79-32bff287aa09') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
.success-message {
  animation: slideIn 0.5s ease-in-out;
}
@keyframes slideIn {
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
button .fa-spinner {
  font-size: 1rem;
}
</style>